/**
 * borrowed/stripped down version of dashboard/forms/FormThemeEditorStartPage/FormThemeEditorStartPage.tsx
 */
import { Color, Spacing } from '@persona-id/design-tokens';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import { RangeInput } from 'shared/components/FormControl';
import styled from 'styled-components';

import { FileUpload } from 'dashboard/components/FormControls/FormControl';
import ThemeEditorFormActionFooter, {
  ACTION_FOOTER_HEIGHT,
} from 'dashboard/components/ThemeEditorFormActionFooter';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import { FORM_THEME_EDITOR_START_PAGE_LOGO_NAME } from 'dashboard/lib/constants/formNames';
import Spacer from 'design-system/components/Spacer';
import { StyleValueJustify } from 'lib/inquiryStyles';
import {
  ImageHostedComponentConfig,
  TitleComponentConfig,
  UiComponentConfigType,
} from 'lib/interfaces/ui-components';

const ACCEPTED_IMAGE_FORMATS = ['image/png', 'image/gif', 'image/jpeg', 'image/svg+xml'];

const Title = styled('h2')`
  color: ${Color.PrimaryBlack};
  padding-top: ${Spacing[7]};
  padding-left: ${Spacing[4]};
`;

const Subtitle = styled('p')`
  display: inline-block;
  border-bottom: 1px solid ${Color.CoreSlate50};
  margin-top: ${Spacing[2]};
  padding-bottom: ${Spacing[5]};
  padding-left: ${Spacing[4]};
  padding-right: ${Spacing[4]};
`;

const Section = styled('div')`
  padding: ${Spacing[4]};
  margin-bottom: ${ACTION_FOOTER_HEIGHT};
`;

const StyledLabel = styled('label')`
  display: inline-block;
`;

const FILE_UPLOAD_LABEL = (
  <>
    <p>Drag and drop your logo here</p>
    <p>PNG, SVG, GIF, or JPEG only</p>
  </>
);

interface FormThemeEditorStartPageLogoProps {
  name: string;
  previewPointer: {
    currentLogoUrl: string | undefined;
    titleTextPreview: string | undefined;
  };
  disabled?: boolean;
}

const FormThemeEditorStartPageLogo = (
  props: FormThemeEditorStartPageLogoProps & InjectedFormProps,
) => {
  const startTitleFallbackKey = 'inquiry.ui.step_template.start.title';
  const { currentLogoUrl, titleTextPreview } = props.previewPointer;
  const handleFileChange = (file?: FileWithPreview) => {
    if (file) {
      // return either image_hosted or title config if uploaded file is present
      const imageHostedConfig: ImageHostedComponentConfig = {
        name: 'logo_image_hosted',
        type: UiComponentConfigType.ImageHosted,
        config: {
          width: '50px',
          hidden: null,
          'hidden-server-evaluated': null,
        },
        'style-variants': [
          {
            namespace: 'base',
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            margin: null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            height: null,
            width: { unit: 'px', value: 50 },
            justify: StyleValueJustify.Start,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'fill-color--fill': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'fill-color--original-fill': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'fill-color--highlight': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'fill-color--original-highlight': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'fill-color--background': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'fill-color--original-background': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'stroke-color--stroke': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'stroke-color--original-stroke': null,
          },
        ],
      };
      props.change(props.name, imageHostedConfig);
    } else {
      const titleConfig: TitleComponentConfig = {
        name: 'start_header_title',
        type: UiComponentConfigType.Title,
        config: {
          'text-fallback-i18n-key': startTitleFallbackKey,
          hidden: null,
          'hidden-server-evaluated': null,
        },
        'style-variants': [
          {
            namespace: 'base',
            margin: { top: null, left: null, right: null, bottom: null },
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            justify: null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'font-family': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'font-size': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'font-weight': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'letter-spacing': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'line-height': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'text-color': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'text-color-highlight': null,
          },
        ],
      };
      props.change(props.name, titleConfig);
    }
  };

  return (
    <form onSubmit={props.handleSubmit}>
      <Title>Start Page Title</Title>
      <Subtitle>
        The start page title can be replaced with an image or logo of your choice.
        <Spacer height={2} />
        Update by clicking on or dragging an image file into the box below to upload!
      </Subtitle>
      <Section>
        <Field
          component={FileUpload}
          label={FILE_UPLOAD_LABEL}
          labelDrag={FILE_UPLOAD_LABEL}
          clearable={titleTextPreview == null}
          name='inqTmplVersion.asset-file'
          accept={ACCEPTED_IMAGE_FORMATS}
          onChange={((file?: FileWithPreview) => handleFileChange(file)) as any}
          preview={
            currentLogoUrl
              ? {
                  isImage: true,
                  preview: currentLogoUrl,
                }
              : titleTextPreview
                ? {
                    isImage: false,
                    preview: titleTextPreview,
                  }
                : null
          }
        />
        <Spacer height={3} />

        {currentLogoUrl != null && (
          <>
            <StyledLabel htmlFor={`${props.name}.config.width`}>Logo width</StyledLabel>
            <Field
              min={50}
              max={352}
              unit='px'
              name={`${props.name}.config.width`}
              component={RangeInput}
              format={(value: string) => (value ? value.substr(0, value.length - 2) : '')}
              normalize={(value: number) => `${value}px`}
              disabled={currentLogoUrl == null}
              onChange={
                // Double write config dimensions and SST dimensions
                ((value: number) =>
                  props.change(`${props.name}.style-variants[0].width`, {
                    unit: 'px',
                    value,
                    // Inconsequential redux-form error related to passing onChange
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  })) as any
              }
            />
          </>
        )}
      </Section>
      {!props.disabled && (
        <ThemeEditorFormActionFooter
          dataTestBlock='form-theme-editor-start-page-title'
          handleUndo={props.reset}
          submitting={props.submitting}
          pristine={props.pristine}
        />
      )}
    </form>
  );
};

export default reduxForm<{}, FormThemeEditorStartPageLogoProps>({
  form: FORM_THEME_EDITOR_START_PAGE_LOGO_NAME,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormThemeEditorStartPageLogo);
